// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/SettingsPage.tsx +
// components/PolicyCard.tsx (ADR-0005) — restyled on the estate tokens (ADR-0010).
// Ids became UUID strings, and the SCOPE moved with the platform (ADR-0002):
//   - firm policy lives on ORG settings (`/settings`), not on the client row —
//     the predecessor's per-client policy was really per-firm, and the org IS
//     the firm here;
//   - the change-password card is GONE — passwords, MFA and sessions are
//     accounts' (the platform's), and a second surface would drift;
//   - the predecessor's AdminPage (users, roles, entities, FY bootstrap) is
//     NOT ported: members and roles are console's, clients are the Clients
//     screen's, and FYs are created on the period picker.
// The UI gate mirrors the API's split exactly: governance keys need
// `settings.can_update` (admin), tuning rides `uploads.can_update`.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useParams } from 'react-router'

import { api, ApiError } from '../../api/client'
import { useFormatProfiles, usePeriodSummary } from '../../api/hooks'
import { Chip } from '../../components/badges'
import { InfoTip } from '../../components/InfoTip'
import { Btn } from '../../components/Modal'
import { showToast } from '../../components/toast'
import { useEvaluation } from '../../lib/access'
import { ACCOUNTS_URL } from '../../lib/config'
import { roleLabelOf } from '../../lib/identity'

interface OrgSettings {
  org_id: string
  duplicate_policy: string
  date_window_days: number
  manual_match_policy: string
  strict_pairing: boolean
  policy: {
    maker_checker: Record<string, boolean>
    editable_ledger_fields: string[]
    lock_exports: boolean
    corrections_survive_reupload: boolean
    combo_max_items: number
    combo_budget: number
    combo_max_n: number
    combo_narrow_search: boolean
    combo_date_window_days: number
    combo_max_candidates: number
    combo_budget_per_target: number
    combo_narrow_budget: number
  }
  replayable_ledger_fields: string[]
  actions: string[]
}

function useOrgSettings() {
  return useQuery({
    queryKey: ['org-settings'],
    queryFn: () => api<OrgSettings>('/settings'),
  })
}

function usePatchSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api('/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      showToast({ color: 'green', title: 'Settings updated.' })
      void qc.invalidateQueries({ queryKey: ['org-settings'] })
      void qc.invalidateQueries({ queryKey: ['period-summary'] })
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not update the settings',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })
}

/** One context fact as a slim inline chip (the ChipStrip idiom, for text). */
function FactChip({ label, value }: { label: string; value: ReactNode }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-xs font-bold uppercase text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </span>
  )
}

function Toggle<T extends string>({ value, onChange, options, disabled }: {
  value: T
  onChange: (v: T) => void
  options: { label: string; value: T }[]
  disabled?: boolean
}) {
  return (
    <span className={`inline-flex overflow-hidden rounded-md border border-border text-xs ${disabled ? 'opacity-60' : ''}`}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          className={`px-2.5 py-1.5 ${value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </span>
  )
}

/** Governance settings decide the controls exercised over the preparer's
 *  work — shown to everyone, changed only by an administrator. */
function AdminOnlyNote() {
  return <p className="mt-1.5 text-xs text-muted-foreground">Set by your administrator.</p>
}

function CardShell({ title, tip, children }: {
  title: string
  tip: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <InfoTip>{tip}</InfoTip>
      </div>
      {children}
    </div>
  )
}

const numCls = 'w-36 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary disabled:opacity-60'

const ACTION_LABEL: Record<string, string> = {
  manual_match: 'Manual match — a second person confirms grouped rows',
  explain: 'Explain — a second person approves each reason code',
  duplicate_verdict: 'Duplicate verdict — a second person confirms genuine / not duplicate',
  expected_section: 'Expected-section rule — a second person approves the rule',
  alias: 'Alias group — a second person approves the name mapping',
  ledger_edit: 'Ledger correction — a second person approves the edit',
}

function NumField({ label, value, min, max, step = 1, disabled, onSave }: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  disabled: boolean
  onSave: (v: number) => void
}) {
  const [v, setV] = useState<number>(value)
  return (
    <label className="block text-xs">
      <span className="mb-0.5 block font-medium text-foreground">{label}</span>
      <span className="flex items-center gap-1.5">
        <input
          type="number"
          className={numCls}
          min={min} max={max} step={step}
          value={v}
          disabled={disabled}
          onChange={(e) => setV(Number(e.target.value))}
        />
        {v !== value && !disabled && (
          <Btn size="xs" onClick={() => onSave(v)}>Save</Btn>
        )}
      </span>
    </label>
  )
}

/** Review & edit policy, split along the governance/tuning line the backend
 *  enforces: governance (who checks whom, what may be written, the export
 *  lock) is admin-only; tuning (how hard the engine searches, how corrections
 *  travel) stays writable by admin + preparer. */
function PolicyCard({ s, canEditGovernance, canEditTuning, save, saving }: {
  s: OrgSettings
  canEditGovernance: boolean
  canEditTuning: boolean
  save: (patch: Record<string, unknown>) => void
  saving: boolean
}) {
  const p = s.policy
  return (
    <div className="rounded-lg border border-border p-4">
      <h2 className="mb-1 text-base font-semibold text-foreground">Review &amp; edit policy</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Which actions a second person has to approve, and what the books screen may
        change. Every setting starts at what this product did before it was
        configurable, so leaving them alone changes nothing.
        {!canEditGovernance && ' Governance settings here are set by your administrator.'}
      </p>

      <div className="space-y-1.5">
        {s.actions.map((a) => (
          <label key={a} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              disabled={!canEditGovernance || saving}
              checked={!!p.maker_checker[a]}
              onChange={(e) => save({ maker_checker: { [a]: e.target.checked } })}
            />
            {ACTION_LABEL[a] ?? a}
          </label>
        ))}
      </div>

      <h3 className="mb-1 mt-4 text-sm font-semibold text-foreground">Editable books fields</h3>
      <div className="flex flex-wrap gap-4">
        {s.replayable_ledger_fields.map((f) => (
          <label key={f} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              disabled={!canEditGovernance || saving}
              checked={p.editable_ledger_fields.includes(f)}
              onChange={(e) => {
                const next = new Set(p.editable_ledger_fields)
                if (e.target.checked) next.add(f)
                else next.delete(f)
                save({ editable_ledger_fields: [...next] })
              }}
            />
            {f}
          </label>
        ))}
      </div>
      <p className="mt-2 rounded-md bg-muted p-2 text-xs text-foreground">
        Only fields the engine can replay into a run are offered. A field it cannot
        replay would let a correction be saved and then ignored by every
        reconciliation — worse than not offering it. 26AS is never editable.
      </p>

      <h3 className="mb-1 mt-4 text-sm font-semibold text-foreground">Exports</h3>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          disabled={!canEditGovernance || saving}
          checked={p.lock_exports}
          onChange={(e) => save({ lock_exports: e.target.checked })}
        />
        Lock exports until the run&apos;s open items are cleared
      </label>
      <p className="mt-1 text-xs text-muted-foreground">
        Off by default. On: report downloads are refused while exceptions remain open,
        so a half-reviewed workbook cannot leave the office.
        {!canEditGovernance && ' Administrators only.'}
      </p>

      <h3 className="mb-1 mt-4 text-sm font-semibold text-foreground">Matching effort</h3>
      <p className="mb-2 text-xs text-muted-foreground">
        How hard the engine looks for n:m combinations. Higher finds more, and takes
        longer; this is a judgement about your client&apos;s data, not a universal setting.
      </p>
      <div className="flex flex-wrap gap-3">
        <NumField label="Max entries a side" min={10} max={5000} disabled={!canEditTuning}
          value={p.combo_max_items} onSave={(v) => save({ combo_max_items: v })} />
        <NumField label="Max entries per group" min={2} max={8} disabled={!canEditTuning}
          value={p.combo_max_n} onSave={(v) => save({ combo_max_n: v })} />
        <NumField label="Search budget" min={1000} max={50000000} step={50000}
          disabled={!canEditTuning}
          value={p.combo_budget} onSave={(v) => save({ combo_budget: v })} />
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            disabled={!canEditGovernance || saving}
            checked={p.combo_narrow_search}
            onChange={(e) => save({ combo_narrow_search: e.target.checked })}
          />
          Narrowed search for parties too large for the bounded pass
        </label>
        <NumField label="Narrow date window (± days)" min={0} max={366}
          disabled={!canEditGovernance || !p.combo_narrow_search}
          value={p.combo_date_window_days}
          onSave={(v) => save({ combo_date_window_days: v })} />
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        <NumField label="Narrow candidates per receipt" min={10} max={2000}
          disabled={!canEditGovernance || !p.combo_narrow_search}
          value={p.combo_max_candidates}
          onSave={(v) => save({ combo_max_candidates: v })} />
        <NumField label="Search steps per receipt" min={100} max={1000000} step={100}
          disabled={!canEditGovernance || !p.combo_narrow_search}
          value={p.combo_budget_per_target}
          onSave={(v) => save({ combo_budget_per_target: v })} />
        <NumField label="Narrow search budget" min={10000} max={100000000} step={100000}
          disabled={!canEditGovernance || !p.combo_narrow_search}
          value={p.combo_narrow_budget}
          onSave={(v) => save({ combo_narrow_budget: v })} />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Off by default, and deliberately so: the narrowed search finds matches the
        engine has never found — a per-client judgement to take with the before/after
        in hand, not a default. Enabled by an administrator only. When a receipt hits
        the per-receipt step cap or the run-wide budget, the worklist says so on the
        party — these knobs decide how far it looks before saying it.
      </p>

      <h3 className="mb-1 mt-4 text-sm font-semibold text-foreground">Corrections</h3>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          disabled={!canEditTuning || saving}
          checked={p.corrections_survive_reupload}
          onChange={(e) => save({ corrections_survive_reupload: e.target.checked })}
        />
        Keep books corrections and duplicate verdicts when the ledger is uploaded again
      </label>
      <p className="mt-1 text-xs text-muted-foreground">
        On by default: re-exporting the ledger should not silently undo work already
        done — corrections carry by row content, and approved duplicate verdicts carry
        with their original recorder and checker. Anything that cannot be carried
        across is reported, never dropped quietly.
      </p>
    </div>
  )
}

/** Saved column mappings — created from the upload page's mapping dialog when
 *  a file doesn't auto-parse; the newest per document kind auto-applies. */
function FormatProfilesCard({ clientId, canEdit }: {
  clientId: string | null
  canEdit: boolean
}) {
  const profilesQ = useFormatProfiles(clientId)
  const queryClient = useQueryClient()
  const del = useMutation({
    mutationFn: (id: string) =>
      api<void>(`/clients/${clientId}/format-profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['format-profiles', clientId] })
      showToast({ color: 'green', title: 'Format profile deleted' })
    },
    onError: (e) => showToast({ color: 'red', title: String(e) }),
  })
  const profiles = profilesQ.data ?? []

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center gap-1">
        <h3 className="text-sm font-semibold text-foreground">Format profiles</h3>
        <InfoTip>
          Saved column mappings for this client&apos;s file layouts. When a file doesn&apos;t
          parse, the upload page offers a mapping dialog — saved mappings apply
          automatically to the next upload of that kind (newest wins).
        </InfoTip>
      </div>
      {profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          None yet — they appear after the first &quot;Map columns&quot; on an upload.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">Name</th>
              <th className="px-2 py-1.5 font-medium">Applies to</th>
              <th className="px-2 py-1.5 font-medium">Shape</th>
              <th className="px-2 py-1.5 font-medium">Sheet · header row</th>
              <th className="px-2 py-1.5 font-medium">Mapped fields</th>
              {canEdit && <th className="px-2 py-1.5" />}
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-2 py-1.5 font-semibold">{p.name}</td>
                <td className="px-2 py-1.5">
                  <Chip tone="blue">{p.doc_kind === 'source' ? '26AS / statement' : 'Books ledger'}</Chip>
                </td>
                <td className="px-2 py-1.5">{p.kind}</td>
                <td className="px-2 py-1.5">{p.sheet ?? '(first sheet)'} · row {p.header_row + 1}</td>
                <td className="px-2 py-1.5 text-muted-foreground">
                  {Object.keys(p.column_map).join(', ')}
                </td>
                {canEdit && (
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      className="rounded p-1 text-red-600 hover:bg-red-50"
                      aria-label="Delete profile"
                      onClick={() => del.mutate(p.id)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function SettingsPage() {
  const { periodId } = useParams()
  const summaryQ = usePeriodSummary(periodId ?? '')
  const { evaluation, can } = useEvaluation()
  const role = roleLabelOf(evaluation)
  const s = summaryQ.data
  const orgQ = useOrgSettings()
  const patch = usePatchSettings()
  const os = orgQ.data

  const canGovern = can('settings', 'can_update')
  const canTune = can('uploads', 'can_update')

  return (
    <div className="space-y-3">
      {/* the read-only context is one chip row, not a card */}
      <div className="flex flex-wrap items-baseline gap-2">
        <h1 className="text-base font-semibold text-foreground">Org settings</h1>
        <InfoTip>
          These switches are the FIRM&apos;s — one answer for every client and every
          FY. Members, roles and passwords are the platform&apos;s: manage members on
          the org console, and your own sign-in at{' '}
          <a className="text-primary underline" href={ACCOUNTS_URL}>accounts</a>.
          Every change here is audited.
        </InfoTip>
      </div>
      {s && (
        <div className="flex flex-wrap gap-8">
          <FactChip label="Client" value={s.client.name} />
          <FactChip label="Period" value={s.period.label} />
          <FactChip label="Your role" value={<Chip tone="blue">{role}</Chip>} />
        </div>
      )}

      {/* governance cards: these decide the controls exercised over the
          preparer's work, so the preparer cannot flip them. */}
      {os && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <CardShell title="Duplicate handling" tip={
            <>Repeated identical entries (same party, date, amount, TDS) are flagged on Raw
            Data — never deleted. <b>Warn</b> shows a banner and lets runs proceed;{' '}
            <b>Block</b> refuses new reconciliation runs until every flagged group is
            reviewed as <i>Genuine duplicate</i> or <i>Not a duplicate</i>.</>
          }>
            <Toggle
              value={os.duplicate_policy}
              disabled={!canGovern}
              onChange={(v) => patch.mutate({ duplicate_policy: v })}
              options={[
                { value: 'warn', label: 'Warn (default)' },
                { value: 'block', label: 'Block until reviewed' },
              ]}
            />
            {!canGovern && <AdminOnlyNote />}
          </CardShell>

          <CardShell title="Manual matching approval" tip={
            <>When a user groups rows on the sheet, does the match count at once or
            wait for a second person? Either way the match is recorded with the user
            who made it. <b>Immediate</b> applies it straight away; <b>Reviewer
            approval</b> holds it pending until a reviewer approves it (roles are
            assigned on the org console).</>
          }>
            <Toggle
              value={os.manual_match_policy}
              disabled={!canGovern}
              onChange={(v) => patch.mutate({ manual_match_policy: v })}
              options={[
                { value: 'immediate', label: 'Immediate' },
                { value: 'maker_checker', label: 'Reviewer approval' },
              ]}
            />
            {!canGovern && <AdminOnlyNote />}
          </CardShell>

          <CardShell title="Pairing strictness" tip={
            <>When a party&apos;s leftover entries don&apos;t pair 1:1 but their totals happen
            to net, <b>Lenient</b> rolls them up into one matched row. <b>Strict</b>{' '}
            shows each leftover as an open line to review instead — use it when
            coincidental nets shouldn&apos;t count as matched. Applies to the next run.</>
          }>
            <Toggle
              value={os.strict_pairing ? 'strict' : 'lenient'}
              disabled={!canGovern}
              onChange={(v) => patch.mutate({ strict_pairing: v === 'strict' })}
              options={[
                { value: 'lenient', label: 'Lenient (roll up nets)' },
                { value: 'strict', label: 'Strict (show residuals)' },
              ]}
            />
            {!canGovern && <AdminOnlyNote />}
          </CardShell>

          <CardShell title="Difference view — date match window" tip={
            <>In the merged 26AS ↔ Books difference view, a matched pair&apos;s dates count
            as agreeing when the 26AS transaction date and the books posting date fall
            within this many days of each other (TDS is deducted on one date and booked
            on another). Wider = fewer date-flag mismatches. Applies to the next run.</>
          }>
            <NumField label="± days" min={0} max={366}
              disabled={!canTune}
              value={os.date_window_days}
              onSave={(v) => patch.mutate({ date_window_days: v })} />
          </CardShell>
        </div>
      )}

      {os && (
        <PolicyCard
          s={os}
          canEditGovernance={canGovern}
          canEditTuning={canTune}
          save={(p) => patch.mutate(p)}
          saving={patch.isPending}
        />
      )}

      <FormatProfilesCard clientId={s?.client.id ?? null} canEdit={canTune} />
    </div>
  )
}
